import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import type {
  RegistrationAnswer,
  RegistrationQuestion,
} from '@/components/events/public-registration-questions'
import type { EventCheckoutOrder } from '@/components/events/public-event-receipt'
import { postJSON } from '@/lib/api'
import type { VerifiedEventBuyer } from '@/components/events/member-ticket-verification'

export function usePublicEventRegistration({
  eventId,
  selections,
  registrationQuestions,
  requiresPayment,
  paymentMethod,
  memberVerification,
  discountCode,
  quotedTotalCents,
}: {
  eventId: number
  selections: { ticket_type_id: number; quantity: number }[]
  registrationQuestions: RegistrationQuestion[]
  requiresPayment: boolean
  paymentMethod: 'online' | 'invoice'
  memberVerification: VerifiedEventBuyer | null
  discountCode: string | null
  quotedTotalCents: number | null
}) {
  const quantity = selections.reduce((total, selection) => total + selection.quantity, 0)
  const idempotencyKey = React.useRef(crypto.randomUUID())
  const orderQuestions = registrationQuestions.filter(
    (question) => question.scope !== 'per_attendee',
  )
  const attendeeQuestions = registrationQuestions.filter(
    (question) => question.scope === 'per_attendee',
  )
  const [buyerName, setBuyerName] = React.useState(memberVerification?.buyer.name ?? '')
  const [buyerEmail, setBuyerEmail] = React.useState(memberVerification?.buyer.email ?? '')
  const [website, setWebsite] = React.useState('')
  const [answers, setAnswers] = React.useState<Record<number, RegistrationAnswer>>({})
  const [attendeeAnswers, setAttendeeAnswers] = React.useState<
    Record<number, RegistrationAnswer>[]
  >(() => Array.from({ length: quantity }, () => ({})))
  const [attendees, setAttendees] = React.useState<string[]>(() =>
    Array.from({ length: quantity }, () => ''),
  )
  const register = useMutation({
    mutationFn: () =>
      postJSON<{
        order?: EventCheckoutOrder
        checkout_session?: { checkout_url: string; processor_session_id: string }
      }>(
        requiresPayment && paymentMethod === 'online'
          ? `/api/v1/public/events/${eventId}/card_checkouts`
          : requiresPayment
            ? `/api/v1/public/events/${eventId}/pay_later_orders`
            : `/api/v1/public/events/${eventId}/orders`,
        {
          order: {
            selections,
            idempotency_key: idempotencyKey.current,
            buyer_name: buyerName,
            buyer_email: buyerEmail,
            ...(discountCode ? { discount_code: discountCode } : {}),
            ...(quotedTotalCents === null ? {} : { quoted_total_cents: quotedTotalCents }),
            ...(memberVerification ? { member_verification_token: memberVerification.token } : {}),
            website,
            attendees: attendees.map((name, index) => ({
              name,
              answers: attendeeQuestions.map((question) => ({
                registration_question_id: question.id,
                value:
                  attendeeAnswers[index]?.[question.id] ??
                  (question.kind === 'multiple_choice' ? [] : ''),
              })),
            })),
            answers: orderQuestions.map((question) => ({
              registration_question_id: question.id,
              value: answers[question.id] ?? (question.kind === 'multiple_choice' ? [] : ''),
            })),
          },
        },
      ),
  })
  return {
    orderQuestions,
    attendeeQuestions,
    buyerName,
    setBuyerName,
    buyerEmail,
    setBuyerEmail,
    website,
    setWebsite,
    answers,
    setAnswers,
    attendeeAnswers,
    setAttendeeAnswers,
    attendees,
    setAttendees,
    register,
  }
}
